import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import FormFieldContext from 'components/forms/FormFieldContext';
import { Task, TaskType, createNewTask } from '../../views/tasks/TaskTypes';
import { useFetchData } from 'services/api/useFetchData';
import axios, { CancelTokenSource } from 'axios';
import TaskFormFields from '../../views/tasks/TaskFormFields';
import { UserType } from '../../views/users/UserTypes';
import { v4 as uuidv4 } from 'uuid';
import '../../style/scss/timeline.scss';
import { saveData } from 'services/api/saveData';
import { TimelineItemType } from 'types/TimelineTypes';

interface TaskFormProps {
    viewKey?: string;
    placement: 'inline' | 'modal';
    timeline_page?: string;  // The object name of the timeline page, for example: 'organization'
    timeline_page_id?: number;  // The id of the timeline page, for example organization '1'
    taskId?: number;
    closeForm?: () => void;
    onSubmitSuccess?: (newItem: TimelineItemType) => void;
}

const TaskForm: React.FC<TaskFormProps> = ({ 
    viewKey, placement, timeline_page, timeline_page_id, taskId, closeForm, onSubmitSuccess
}) => {
    const { userHash } = useAuthContext();
    const { setFloatingAlert, setUnsavedChanges, unsavedChangesMap, setErrorMessages } = useGlobalContext();
    const { modalStack, revealModal, closeModal } = useModal();
    const [originalTaskData, setOriginalTaskData] = useState<Task | null>(null);
    const [taskData, setTaskData] = useState<Task | null>(null);
    const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
    const [assignees, setAssignees] = useState<UserType[]>([]);
    const [cancelSource, setCancelSource] = useState<CancelTokenSource | null>(null);
    const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
    const [buttonLoader, setButtonLoader] = useState(false);
    const topModal = modalStack[modalStack.length - 1];
    const showInModal = placement === 'modal';

    // Fetch the data of the current task
    const { response: fetchedTaskData, loading: taskLoading } = useFetchData({ 
        apiUrl: taskId ? `get_task/${taskId}` : '', 
        useGuard: true,
        guardValue: taskId
    });

    // Fetch the task types
    const { response: fetchedTaskTypes, loading: taskTypesLoading } = useFetchData({ 
        apiUrl: 'get_tasktype_list', 
        params: useMemo(() => ({ deleted: false }), []) 
    });

    // Fetch the assignees
    const { response: fetchedAssignees, loading: assigneesLoading } = useFetchData({ 
        apiUrl: 'get_user_list', 
        params: useMemo(() => ({ is_active: true, office_access: true }), []) 
    });

    // Set the fetched task types
    useEffect(() => {
        if (fetchedTaskTypes) {
            // Order the task types on the ordering field
            const sortedTaskTypes = [...fetchedTaskTypes.results].sort((a, b) => (a.ordering || 0) - (b.ordering || 0));

            // Set the task types
            setTaskTypes(sortedTaskTypes);
        };
    }, [fetchedTaskTypes]);

    // Set the fetched assignees
    useEffect(() => {
        if (fetchedAssignees) {
            setAssignees(fetchedAssignees.results);
        };
    }, [fetchedAssignees]);

    // Set the task data, default task type and default assignee
    useEffect(() => {
        if (taskData !== null) return;

        // Set the task data for existing tasks
        if (taskId && fetchedTaskData) {
            setTaskData(fetchedTaskData);
            setOriginalTaskData(fetchedTaskData);

            if (showInModal) {
                revealModal();
            }

            return;
        }

        // Create a new task
        if (!taskId && fetchedAssignees) {

            // Set the current user as default assignee
            let defaultAssignee: UserType | null = null;
            const currentUser = fetchedAssignees.results.find((user: UserType) => user.user_hash === userHash);
            if (currentUser) {
                defaultAssignee = currentUser;

                setUpdatedData((prev: Task) => ({ ...prev, ['assignee']: defaultAssignee!.id }));
            }

            // Create the new task
            const newTask = createNewTask(uuidv4(), defaultAssignee);

            setTaskData(newTask);
            setOriginalTaskData(newTask);

            if (showInModal) {
                revealModal();
            }
        }
    }, [taskId, taskData, fetchedTaskData, fetchedTaskTypes, fetchedAssignees]);

    // Set unsaved changes when updated data changes
    useEffect(() => {
        if (!originalTaskData || !updatedData) return;

        // Check if the updated data contains different values than the original task data
        const hasChanges = Object.entries(updatedData).some(([key, value]) => {
            return originalTaskData[key as keyof Task] !== value;
        });

        // Set unsaved changes
        if (viewKey && (unsavedChangesMap[viewKey] !== hasChanges) && showInModal) {
            setUnsavedChanges(viewKey, hasChanges);
        }
    }, [originalTaskData, updatedData]);
    
    // Handle delete
    const handleDelete = async () => {
        if (taskData && taskData.id) {
            try {
                // Call the delete task function on the backend
                const response = await saveData({ 
                    apiUrl: `delete_task/${taskData.id}`,
                    method: 'patch',
                    data: {},
                    source: axios.CancelToken.source()
                })

                // Handle successful response
                if (response && response.status === 200) {
                    // Show success alert
                    setFloatingAlert({ 'type': 'success' });

                    // Update the timeline items state
                    onSubmitSuccess?.({
                        ...response.data,
                        timeline_object_type: 'task'
                    });

                    closeForm?.();
        
                    if (showInModal) {
                        closeModal();
                    }
                }
            } catch (error) {
                setFloatingAlert({ type: 'danger', message: 'timeline.task.alert.delete_task_failed_message' })
            }
        }
    }

    // Handle submit
    const handleSubmit = async () => {
        setButtonLoader(true);

        // Create the cancel token source
        const source = axios.CancelToken.source();
        setCancelSource(source);

        // Add the given timeline page and timeline page id to the submit data
        const submitData = {
            ...updatedData,
            [timeline_page as string]: timeline_page_id,
            ...(taskId ? { modified_by_user_hash: userHash } : { created_by_user_hash: userHash })
        }

        // Configure the save data props
        const apiUrl = taskId ? `patch_task/${taskId}` : 'post_task';
        const method = taskId ? 'patch' : 'post';

        try {
            // Save the data in the backend
            const response = await saveData({ apiUrl, method, data: submitData, source });

            // Handle successful response
            if (response && (response.status === 200 || response.status === 201)) {
                // Show success alert
                setFloatingAlert({ 'type': 'success' });

                setButtonLoader(false);

                // Update the timeline items state
                onSubmitSuccess?.({
                    ...response.data,
                    timeline_object_type: 'task'
                });

                if (viewKey) {
                    setUnsavedChanges(viewKey, false);
                }

                closeForm?.();

                if (showInModal) {
                    // Call the on success callback to refresh the list
                    if (topModal?.props?.onSuccess) {
                        topModal.props.onSuccess(response.data);
                    };

                    closeModal();
                }
            }
        } catch (error: any) {
            if (error.response && error.response.status === 400) {
                setErrorMessages(error.response.data);
            } else {
                setFloatingAlert({ type: 'danger', message: 'validation.general.save_failed' })
            }

            setButtonLoader(false);
        }
    };

    // Handles the closing of the note form
    const handleClose = () => {
        if (cancelSource) {
            cancelSource.cancel();
        }

        closeForm?.();
        setErrorMessages({});
        setButtonLoader(false);

        if (viewKey) {
            setUnsavedChanges(viewKey, false);
        }

        if (showInModal) {
            closeModal();
        }
    };

    // Determine to show the loader
    const shouldShowLoader =
        taskTypesLoading !== 'success' ||
        assigneesLoading !== 'success' ||
        (taskId && taskLoading !== 'success');

    return (
        (shouldShowLoader || !taskData) && placement === 'inline' ? (
            <div className='inline-form-loader'>
                <div className="loader"></div>
            </div>
        ) : (
            taskData && (
                <FormFieldContext.Provider value={{ updatedData, setUpdatedData }}>
                    <TaskFormFields 
                        placement={placement}
                        data={taskData}
                        setData={setTaskData}
                        taskTypes={taskTypes}
                        assignees={assignees}
                        buttonLoader={buttonLoader}
                        handleClose={handleClose}
                        handleDelete={handleDelete}
                        handleSubmit={handleSubmit}
                    />
                </FormFieldContext.Provider>
            )
        )
    );
};

export default TaskForm;